import { useEffect, useState } from 'react';
import imageCache from '../utils/image-cache';

/**
 * React hook to load and cache images
 * @param src - The image URL to load
 * @param enabled - Whether to load the image (for lazy loading support)
 * @returns The cached image URL (blob URL) or the original URL while loading
 */
export function useImageCache(src: string | undefined, enabled = true) {
    // Check cache synchronously on initial render to avoid unnecessary network requests
    const [cachedSrc, setCachedSrc] = useState<string | undefined>(() => {
        if (!enabled || !src) return src;
        return imageCache.getCached(src) || src;
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        // Don't load if disabled or no src
        if (!enabled || !src) {
            setCachedSrc(src);
            setLoading(false);
            return;
        }

        // If not an API URL, just use it directly
        if (!src.includes('/api/internal/')) {
            setCachedSrc(src);
            setLoading(false);
            return;
        }

        // Check if already in cache (synchronous check)
        const cached = imageCache.getCached(src);
        if (cached) {
            setCachedSrc(cached);
            setLoading(false);
            return;
        }

        // Load and cache the image
        setLoading(true);
        imageCache
            .get(src)
            .then((blobUrl) => {
                if (!cancelled) {
                    setCachedSrc(blobUrl);
                    setLoading(false);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error('Failed to cache image:', error);
                    // Fallback to original URL
                    setCachedSrc(src);
                    setLoading(false);
                }
            });

        // Cleanup function to prevent state updates after unmount or src change
        return () => {
            cancelled = true;
        };
    }, [src, enabled]);

    return { src: cachedSrc, loading };
}
